import { useRef, useState } from 'react';

import { cn } from '@/utils/cn';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  value: string;
  onChange: (next: string) => void;
  options: SearchableSelectOption[];
  /** Allow committing arbitrary typed text not present in `options`. */
  freeText?: boolean;
  placeholder?: string;
  className?: string;
  ariaLabel?: string;
}

/**
 * Filter-as-you-type single-select combobox, modelled on the EditableField combo
 * (FieldRow.tsx). Two modes:
 *  - fixed list (`freeText` off): the trigger shows the selected option's label,
 *    typing filters, and only listed options can be committed.
 *  - free text (`freeText` on): typing filters `options` (used as suggestions) and
 *    any typed value commits as-is.
 */
export function SearchableSelect({
  value,
  onChange,
  options,
  freeText,
  placeholder,
  className,
  ariaLabel,
}: SearchableSelectProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);

  // Display text for the current value: the matching option's label in fixed mode,
  // the raw value in free-text mode.
  const selectedLabel = freeText
    ? value
    : options.find((o) => o.value === value)?.label ?? '';

  // While open, filter by the typed query; while closed, show the selected label.
  const q = query.trim().toLowerCase();
  const matches = (q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options).slice(0, 50);

  const openList = () => {
    // Free-text mode seeds the current value so it can be edited inline; the fixed
    // list opens empty so every option is visible until the user types to filter.
    setQuery(freeText ? selectedLabel : '');
    setHighlight(0);
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const commit = (next: string) => {
    onChange(next);
    close();
    inputRef.current?.blur();
  };

  const commitHighlight = () => {
    if (matches[highlight]) commit(matches[highlight].value);
    else if (freeText) commit(query);
    else close();
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        className={cn('ds-select', className)}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={open ? query : selectedLabel}
        onFocus={openList}
        onClick={openList}
        // Defer close so an option's onMouseDown fires before blur.
        onBlur={() => window.setTimeout(close, 120)}
        onChange={(e) => {
          setQuery(e.target.value);
          setHighlight(0);
          setOpen(true);
          if (freeText) onChange(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setHighlight((h) => Math.min(h + 1, matches.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            commitHighlight();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            close();
          }
        }}
      />
      {open && matches.length > 0 && (
        <div
          className="ds-popover"
          style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, padding: 4, zIndex: 20, maxHeight: 240, overflowY: 'auto' }}
        >
          {matches.map((o, i) => (
            <button
              key={o.value}
              type="button"
              className="ds-menu-item"
              style={i === highlight ? { background: 'var(--surface-hover)' } : undefined}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                commit(o.value);
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
